'use client';

import React from 'react';
import { Check } from 'lucide-react';
import RemindersBadge from '@/components/RemindersBadge';

const getBouncedRecipients = (envelope) =>
  envelope.recipients?.filter(r => r.status?.toLowerCase() === 'autoresponded') || [];

// Build status description based on envelope state and recipients
const getStatusDescription = (envelope, needsUserSignature) => {
  const status = envelope.status?.toLowerCase();

  // Terminal states
  if (status === 'completed') return 'Completed';
  if (status === 'declined') return 'Declined';
  if (status === 'voided') return 'Voided';
  if (status === 'deleted') return 'Deleted';

  // If current user needs to sign
  if (needsUserSignature) return 'You need to sign';

  // A bounced invite (DocuSign 'autoresponded') must show from the LIST - otherwise the
  // packet reads 'Processing' or 'Waiting for' and nobody notices the patient never got it.
  const bouncedRecipients = getBouncedRecipients(envelope);
  if (bouncedRecipients.length === 1) {
    const name = bouncedRecipients[0].name || bouncedRecipients[0].email?.split('@')[0] || 'recipient';
    return `Email failed to deliver to ${name}`;
  }
  if (bouncedRecipients.length > 1) {
    return `Email failed to deliver to ${bouncedRecipients.length} signers`;
  }

  // Find recipients who need to sign
  const awaitingRecipients = envelope.recipients?.filter(r =>
    ['sent', 'delivered'].includes(r.status?.toLowerCase())
  ) || [];

  if (awaitingRecipients.length === 0) {
    // No one awaiting - check if draft
    if (status === 'created') return 'Draft';
    return 'Processing';
  }

  if (awaitingRecipients.length === 1) {
    const name = awaitingRecipients[0].name || awaitingRecipients[0].email?.split('@')[0] || 'recipient';
    return `Waiting for ${name}`;
  }

  return `Waiting for ${awaitingRecipients.length} signers`;
};

// Mini progress bar component
const SignerProgressBar = ({ recipients, formatDate }) => {
  if (!recipients || recipients.length === 0) return null;

  // Sort by routing order
  const sorted = [...recipients].sort((a, b) => (a.routingOrder ?? 999) - (b.routingOrder ?? 999));

  // Check if sequential signing (has routing order > 1)
  const isSequential = sorted.some(r => r.routingOrder > 1);

  // Count completed
  const completed = sorted.filter(r => r.status === 'completed').length;
  const total = sorted.length;

  // Determine status for each recipient
  const getRecipientState = (recipient, idx) => {
    if (recipient.status === 'completed') return 'completed';
    if (['sent', 'delivered'].includes(recipient.status?.toLowerCase())) return 'current';
    if (['autoresponded', 'declined'].includes(recipient.status?.toLowerCase())) return 'failed';
    return 'pending';
  };

  // Build enhanced tooltip for recipient
  const buildTooltip = (recipient, state) => {
    const name = recipient.name || recipient.email || 'Unknown';
    const statusText = state === 'completed' ? 'Signed'
      : state === 'current' ? 'Awaiting'
      : state === 'failed' ? (recipient.status?.toLowerCase() === 'declined' ? 'Declined' : 'Failed to deliver')
      : 'Pending';
    let tooltip = `${name}\nStatus: ${statusText}`;

    if (state === 'completed' && recipient.timestamps?.signedDateTime) {
      tooltip += `\nSigned: ${formatDate ? formatDate(recipient.timestamps.signedDateTime) : recipient.timestamps.signedDateTime}`;
    } else if (state === 'current' && recipient.timestamps?.sentDateTime) {
      tooltip += `\nSent: ${formatDate ? formatDate(recipient.timestamps.sentDateTime) : recipient.timestamps.sentDateTime}`;
    }

    return tooltip;
  };

  // Find current signer index (first non-completed)
  const currentIndex = sorted.findIndex(r => r.status !== 'completed');

  // Limit display to 5 recipients
  const displayRecipients = sorted.slice(0, 5);
  const hasMore = sorted.length > 5;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center">
        {displayRecipients.map((recipient, idx) => {
          const state = getRecipientState(recipient, idx);
          const isLast = idx === displayRecipients.length - 1;

          return (
            <React.Fragment key={idx}>
              {/* Circle/dot */}
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  state === 'completed'
                    ? 'bg-emerald-500'
                    : state === 'current'
                      ? 'bg-sky-500'
                      : state === 'failed'
                        ? 'bg-red-500'
                        : 'bg-white border-2 border-gray-300'
                }`}
                title={buildTooltip(recipient, state)}
              >
                {state === 'completed' && (
                  <Check className="w-2.5 h-2.5 text-white" />
                )}
                {state === 'current' && (
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                )}
              </div>

              {/* Connecting line (only for sequential, not after last) */}
              {isSequential && !isLast && (
                <div className={`w-2 h-0.5 ${
                  state === 'completed' ? 'bg-emerald-500' : 'bg-gray-200'
                }`} />
              )}

              {/* Small gap for parallel signing */}
              {!isSequential && !isLast && (
                <div className="w-0.5" />
              )}
            </React.Fragment>
          );
        })}

        {/* More indicator */}
        {hasMore && (
          <>
            {isSequential && <div className="w-2 h-0.5 bg-gray-200" />}
            <div className="w-4 h-4 rounded-full flex items-center justify-center bg-gray-100 text-[8px] text-gray-500 font-medium">
              +{sorted.length - 5}
            </div>
          </>
        )}
      </div>

      {/* Count */}
      <span className="text-[10px] text-gray-500 ml-0.5">{completed}/{total}</span>
    </div>
  );
};

const EnvelopeListItem = ({
  envelope,
  isSelected,
  onClick,
  needsUserSignature,
  formatDate
}) => {
  const isTerminalState = ['declined', 'deleted', 'voided'].includes(envelope.status?.toLowerCase());
  const statusDescription = getStatusDescription(envelope, needsUserSignature);
  const hasBounce = !isTerminalState && !needsUserSignature && getBouncedRecipients(envelope).length > 0;

  return (
    <div
      onClick={onClick}
      className={`
        px-3 py-1 border-b border-gray-100 cursor-pointer transition-all duration-100
        ${isSelected
          ? 'bg-cyan-50 border-l-[3px] border-l-[#2db3e1]'
          : 'hover:bg-gray-50 border-l-[3px] border-l-transparent'}
        ${isTerminalState ? 'opacity-50' : ''}
      `}
    >
      {/* Subject row: subject + action badge + signer progress */}
      <div className="flex items-center gap-1.5">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate leading-none">
            {envelope.subject || 'Untitled Document'}
          </p>
        </div>
        {needsUserSignature && !isTerminalState && (
          <span className="flex-shrink-0 text-[10px] font-medium text-[#2db3e1] bg-cyan-50 px-1.5 py-0.5 rounded">
            Sign
          </span>
        )}
        <div className="flex-shrink-0">
          <SignerProgressBar recipients={envelope.recipients} formatDate={formatDate} />
        </div>
      </div>
      {/* Status subtitle */}
      <p className={`text-[10px] leading-none ${needsUserSignature && !isTerminalState ? 'text-[#2db3e1] font-medium' : hasBounce ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
        – {statusDescription}
      </p>
      {/* Silenced reminders must be visible from the LIST, not just once an envelope is
          opened - otherwise a consent quietly stops chasing the patient and nobody sees it. */}
      {!isTerminalState && envelope.remindersEnabled === false && (
        <div className="mt-0.5">
          <RemindersBadge remindersEnabled={envelope.remindersEnabled} />
        </div>
      )}
    </div>
  );
};

export default EnvelopeListItem;
